// src/pages/ambulance/AmbulanceAnalytics.jsx
import React, { useState, useEffect } from 'react';
import {
  TrendingUp,  DollarSign, MapPin, Clock,
  Activity, Users, Ambulance,  AlertCircle,
  CheckCircle, XCircle, BarChart3, PieChart
} from 'lucide-react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AmbulanceAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.ambulances.getStats();
      
      if (response.success) {
        setStats(response.data);
      } else {
        setError(response.message || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Analytics error:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = (current, total) => {
    if (!total || total === 0) return 0;
    return ((current / total) * 100).toFixed(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading analytics..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Analytics</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const ambulanceStats = stats?.ambulance || {};
  const driversStats = stats?.drivers || {};
  const vehiclesStats = stats?.vehicles || {};
  const requestsStats = stats?.requests || {};
  const revenueStats = stats?.revenue || {};
  const subscriptionInfo = stats?.subscription || {};
  const limitsInfo = stats?.limits || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
              <p className="text-gray-600">Monitor your ambulance service performance</p>
            </div>
            
            {/* Time Range Selector */}
            <div className="flex gap-2">
              {['week', 'month', 'year'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    timeRange === range
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Subscription Status */}
        {subscriptionInfo && (
          <div className={`rounded-xl p-6 mb-6 ${
            subscriptionInfo.status === 'active' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-orange-50 border border-orange-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  Subscription: {subscriptionInfo.plan?.toUpperCase() || 'FREE'} Plan
                </h3>
                <p className="text-sm text-gray-600">
                  Status: <span className="font-semibold">{subscriptionInfo.status || 'Active'}</span>
                  {subscriptionInfo.endDate && (
                    <> • Expires: {new Date(subscriptionInfo.endDate).toLocaleDateString()}</>
                  )}
                </p>
              </div>
              <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition">
                Upgrade Plan
              </button>
            </div>
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Trips */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Ambulance className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">
                  {ambulanceStats.totalTrips || 0}
                </p>
                <p className="text-sm text-gray-600">Total Trips</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-green-600 font-medium flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {ambulanceStats.completedTrips || 0} completed
              </span>
            </div>
          </div>

          {/* Completed Trips */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">
                  {ambulanceStats.completedTrips || 0}
                </p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">
                {calculatePercentage(ambulanceStats.completedTrips, ambulanceStats.totalTrips)}% completion rate
              </span>
            </div>
          </div>

          {/* Total Distance */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600">
                  {ambulanceStats.totalDistance?.toFixed(1) || 0}
                </p>
                <p className="text-sm text-gray-600">Total Distance (km)</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">
                Avg: {ambulanceStats.totalTrips > 0 
                  ? (ambulanceStats.totalDistance / ambulanceStats.totalTrips).toFixed(1) 
                  : 0} km/trip
              </span>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(ambulanceStats.totalRevenue || 0)}
                </p>
                <p className="text-sm text-gray-600">Total Revenue</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">
                This month: {formatCurrency(revenueStats.thisMonth || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Resources & Requests Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Resources Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary-600" />
              Resources
            </h3>

            <div className="space-y-4">
              {/* Drivers */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Drivers</span>
                  <span className="text-sm text-gray-600">
                    {driversStats.available || 0} / {driversStats.total || 0} available
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${calculatePercentage(driversStats.available, driversStats.total)}%`
                    }}
                  />
                </div>
                <div className="flex justify-between items-center mt-1 text-xs text-gray-600">
                  <span>Limit: {limitsInfo.maxDrivers || 1}</span>
                  <span>Current: {limitsInfo.currentDrivers || 0}</span>
                </div>
              </div>

              {/* Vehicles */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Vehicles</span>
                  <span className="text-sm text-gray-600">
                    {vehiclesStats.available || 0} / {vehiclesStats.total || 0} available
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${calculatePercentage(vehiclesStats.available, vehiclesStats.total)}%`
                    }}
                  />
                </div>
                <div className="flex justify-between items-center mt-1 text-xs text-gray-600">
                  <span>Limit: {limitsInfo.maxVehicles || 1}</span>
                  <span>Current: {limitsInfo.currentVehicles || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Requests Overview */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary-600" />
              Requests Overview
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-700">Today</span>
                </div>
                <p className="text-2xl font-bold text-yellow-600">
                  {requestsStats.today || 0}
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {requestsStats.active || 0}
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-700">Cancelled</span>
                </div>
                <p className="text-2xl font-bold text-red-600">
                  {ambulanceStats.cancelledTrips || 0}
                </p>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">Avg Response</span>
                </div>
                <p className="text-2xl font-bold text-purple-600">
                  {ambulanceStats.averageResponseTime 
                    ? `${ambulanceStats.averageResponseTime.toFixed(0)}m`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary-600" />
            Performance Metrics
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Completion Rate */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-green-100 mb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {calculatePercentage(ambulanceStats.completedTrips, ambulanceStats.totalTrips)}%
                  </p>
                  <p className="text-xs text-gray-600">Completion</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 font-medium">Trip Completion Rate</p>
            </div>

            {/* Resource Utilization */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-blue-100 mb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">
                    {calculatePercentage(
                      (driversStats.total - driversStats.available),
                      driversStats.total
                    )}%
                  </p>
                  <p className="text-xs text-gray-600">In Use</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 font-medium">Resource Utilization</p>
            </div>

            {/* Revenue Per Trip */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-orange-100 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    {ambulanceStats.totalTrips > 0
                      ? formatCurrency(ambulanceStats.totalRevenue / ambulanceStats.totalTrips)
                      : formatCurrency(0)}
                  </p>
                  <p className="text-xs text-gray-600">Per Trip</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 font-medium">Average Revenue</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
          <h3 className="text-xl font-bold mb-4">Need to improve your metrics?</h3>
          <p className="mb-6 text-primary-100">
            Upgrade your plan to add more vehicles and drivers, or optimize your operations.
          </p>
          <div className="flex gap-4">
            <button className="px-6 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition">
              Upgrade Plan
            </button>
            <button className="px-6 py-3 border border-white text-white rounded-lg font-semibold hover:bg-primary-600 transition">
              View Insights
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbulanceAnalytics;